import axios from 'axios';
import sha256 from 'sha256';
import {
  getAccessToken as _getAccessToken,
  getRefreshToken as _getRefreshToken,
  setTokens,
} from './tokenStore';
import { removeUserSession } from '../Utils/Common';
import Constants from '../Utils/Constants';

// Dedicated client for auth endpoints — deliberately carries NO response interceptors.
// If we used the shared axios instance here, a failed refresh would trigger another
// refresh attempt, creating an infinite loop.
const authClient = axios.create();

// Deduplication: if refresh() is already in-flight, every concurrent caller
// awaits the same promise instead of firing a second HTTP call.
// Prevents the race between proactive rehydration and reactive 401 handling.
let _refreshPromise = null;

const AuthService = {
  /**
   * Sign in with credentials.
   *
   * AUTH_MODE = 'new'    → POST /api/auth/login    (backend owns OAuth/client creds)
   * AUTH_MODE = 'legacy' → POST /oauth/token       (direct Passport flow, exposes client_secret)
   *
   * Both modes return the same shape: { access_token, refresh_token, expires_in }
   */
  login(username, password) {
    if (Constants.authMode === 'legacy') {
      return authClient.post(
        Constants.loginApiPath,
        {
          username,
          password,
          client_id: '2',
          client_secret: 'ur7r3um3W9YwFM3IMpqFpUdcBUnbEJvWLiV8snmt',
          grant_type: 'password',
        },
        { headers: {} }
      );
    }
    const sentPassword = Constants.authHashPassword ? sha256(password) : password;
    return authClient.post(Constants.authLoginPath, { email: username, password: sentPassword });
  },

  /**
   * Exchange the stored refresh token for a new access token.
   *
   * Updates individual storage keys in-place — does NOT call store.clear(),
   * so user data, c_id, and other session keys are preserved across a refresh.
   *
   * Returns the new access token string on success.
   * Throws when no refresh token exists or the server rejects it.
   */
  refresh() {
    if (_refreshPromise) return _refreshPromise;

    const refreshToken = _getRefreshToken();
    if (!refreshToken) return Promise.reject(new Error('No refresh token available'));

    _refreshPromise = authClient
      .post(Constants.authRefreshPath, { refresh_token: refreshToken })
      .then(({ data }) => {
        // Normalise response: handle both { access_token } and { token: { access_token } }
        const tokenData = data.token ?? data;
        setTokens(tokenData.access_token, tokenData.refresh_token || refreshToken);
        return tokenData.access_token;
      })
      .finally(() => {
        _refreshPromise = null;
      });

    return _refreshPromise;
  },

  /**
   * Invalidate the server-side session (best-effort), then clear local storage.
   * Always clears local state even if the server call fails.
   */
  async logout() {
    const token = _getAccessToken();
    if (token) {
      try {
        await authClient.post(
          Constants.authLogoutPath,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (_) {
        // Server-side invalidation is best-effort; local state must still clear.
      }
    }
    removeUserSession();
  },

  getAccessToken() {
    return _getAccessToken();
  },

  isAuthenticated() {
    return !!_getAccessToken();
  },
};

export default AuthService;
